import React, { useRef } from 'react';
import {
  Animated,
  Easing,
  StyleProp,
  StyleSheet,
  ViewStyle,
  View,
} from 'react-native';

import { SkeletonPlaceholder } from './skeleton-placeholder/skeleton-placeholder';
import { useLessonContext } from '../hooks';
import useDidUpdateEffect from '../hooks/use-did-update-effect';
import { useSafeState } from '../hooks/use-safe-state';

interface TranslationSkeletonContainerProps {
  animationDuration?: number;
  hasTranslation: boolean;
  skeletonGradientColor?: string;
  skeletonBackgroundColor?: string;
  skeletonStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
}

const TranslationSkeletonContainer: React.FC<
  TranslationSkeletonContainerProps
> = ({
  animationDuration = 1200,
  hasTranslation,
  children,
  skeletonGradientColor,
  skeletonBackgroundColor,
  skeletonStyle,
  style,
}) => {
  const { translationActive } = useLessonContext();
  const [isSkeletonVisible, setSkeletonVisible] = useSafeState(false);
  const transationAnimation = useRef(new Animated.Value(0)).current;
  const [maxHeight, setMaxHeight] = useSafeState(0);

  useDidUpdateEffect(() => {
    if (!hasTranslation) {
      return;
    }

    setSkeletonVisible(true);
    Animated.timing(transationAnimation, {
      toValue: 2,
      duration: animationDuration / 2,
      easing: Easing.out(Easing.linear),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished) {
        Animated.timing(transationAnimation, {
          toValue: 4,
          duration: animationDuration / 2,
          easing: Easing.out(Easing.linear),
          useNativeDriver: true,
        }).start(({ finished }) => {
          if (finished) {
            setSkeletonVisible(false);
            transationAnimation.setValue(0);
          }
        });
      }
    });
  }, [translationActive, transationAnimation, hasTranslation]);

  const skeletonOpacity = transationAnimation.interpolate({
    inputRange: [0, 1, 3, 3.5],
    outputRange: [0, 1, 1, 0],
    extrapolate: 'clamp',
  });

  const textOpacity = transationAnimation.interpolate({
    inputRange: [0, 0.5, 3, 4],
    outputRange: [1, 0, 0, 1],
    extrapolate: 'clamp',
  });

  return (
    <View>
      <Animated.View
        onLayout={(e) => setMaxHeight(e.nativeEvent.layout.height)}
        style={{ opacity: textOpacity }}
      >
        {children}
      </Animated.View>
      {isSkeletonVisible && (
        <Animated.View
          style={[
            styles.skeletonContainer,
            { opacity: skeletonOpacity, maxHeight },
            style,
          ]}
        >
          <SkeletonPlaceholder
            width="100%"
            backgroundColor={skeletonBackgroundColor}
            gradientColor={skeletonGradientColor}
            style={skeletonStyle}
          />
          {maxHeight > 70 && (
            <SkeletonPlaceholder
              width="70%"
              backgroundColor={skeletonBackgroundColor}
              gradientColor={skeletonGradientColor}
              style={[styles.midSkeleton, skeletonStyle]}
            />
          )}
          {maxHeight > 140 && (
            <SkeletonPlaceholder
              width="100%"
              backgroundColor={skeletonBackgroundColor}
              gradientColor={skeletonGradientColor}
              style={skeletonStyle}
            />
          )}
          {maxHeight > 210 && (
            <SkeletonPlaceholder
              width="40%"
              backgroundColor={skeletonBackgroundColor}
              gradientColor={skeletonGradientColor}
              style={skeletonStyle}
            />
          )}
        </Animated.View>
      )}
    </View>
  );
};

export default TranslationSkeletonContainer;

const styles = StyleSheet.create({
  skeletonContainer: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    overflow: 'hidden',
  },
  midSkeleton: {
    marginBottom: 20,
  },
});
